/**
 * CookieBanner — first-visit cookie consent UI.
 *
 * Storage rules:
 *   • Decision is persisted to localStorage under `si_cookie_consent_v1`.
 *   • Each click (Accept all / Reject non-essential / Save customized choices)
 *     also POSTs to `/api/consents/cookies` so we have a server-side audit
 *     trail (timestamp + IP + user-agent + user_id when authenticated).
 *   • If localStorage is unavailable (private mode strict, etc.) the banner
 *     stays visible but the network call still runs once per click.
 *
 * The banner is intentionally NON-blocking — the rest of the page is fully
 * interactive. Users can re-open it any time via Profile → Communications
 * → Cookies (will be wired in a follow-up).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

const STORAGE_KEY = "si_cookie_consent_v1";
const POLICY_VERSION = "v1.1";

const DEFAULT_PREFS = {
    strictly_necessary: true, // always on, non-toggleable
    functional: false,
    analytics: false,
    marketing: false,
};

function readStored() {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed?.version !== POLICY_VERSION) return null;
        return parsed;
    } catch {
        return null;
    }
}

function writeStored(record) {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch {
        /* ignore — best effort */
    }
}

export default function CookieBanner() {
    const [visible, setVisible] = useState(false);
    const [customizing, setCustomizing] = useState(false);
    const [prefs, setPrefs] = useState(DEFAULT_PREFS);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        // Only render if no stored decision for the current policy version.
        const stored = readStored();
        if (!stored) setVisible(true);
    }, []);

    if (!visible) return null;

    const send = async (action, choice) => {
        setBusy(true);
        const body = {
            ...choice,
            strictly_necessary: true,
            version: POLICY_VERSION,
            action,
        };
        try {
            await api.post("/consents/cookies", body);
        } catch {
            /* network errors must not block the user from dismissing */
        } finally {
            setBusy(false);
        }
        writeStored({
            ...body,
            decided_at: new Date().toISOString(),
        });
        setVisible(false);
    };

    const acceptAll = () => send("accept_all", { functional: true, analytics: true, marketing: true });
    const rejectAll = () => send("reject_all", { functional: false, analytics: false, marketing: false });
    const saveCustom = () => send("customize", {
        functional: !!prefs.functional,
        analytics: !!prefs.analytics,
        marketing: !!prefs.marketing,
    });

    return (
        <div
            className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4 sm:px-6 sm:pb-6 pointer-events-none"
            role="dialog"
            aria-modal="false"
            aria-label="Cookie preferences"
            data-testid="cookie-banner"
        >
            <div className="pointer-events-auto max-w-3xl mx-auto bg-paper border-2 border-ink shadow-[0_8px_0_0_rgba(0,0,0,1)]">
                <div className="p-5 sm:p-6">
                    <div className="flex items-start justify-between gap-4 mb-3">
                        <div>
                            <p className="eyebrow">COOKIES · {POLICY_VERSION}</p>
                            <h3 className="text-xl font-semibold mt-1">Your cookie preferences</h3>
                        </div>
                        <button
                            className="font-mono text-xs underline text-ink-600 hover:text-ink"
                            onClick={() => setCustomizing((v) => !v)}
                            data-testid="cookie-banner-toggle-customize"
                        >
                            {customizing ? "hide details" : "customize"}
                        </button>
                    </div>

                    {!customizing && (
                        <p className="text-sm text-ink-600 leading-relaxed mb-4">
                            We use only first-party, strictly-necessary cookies (login + security). We do not run analytics, marketing, or social-media trackers. You can read the full breakdown in our{" "}
                            <Link to="/legal/cookies" className="underline" data-testid="cookie-banner-policy-link">
                                Cookie Policy
                            </Link>
                            .
                        </p>
                    )}

                    {customizing && (
                        <div className="space-y-3 mb-5" data-testid="cookie-banner-customize-panel">
                            <Toggle
                                label="Strictly necessary"
                                desc="Authentication, security, and load-balancing. Always on — required for the site to function."
                                checked
                                disabled
                                testid="cookie-toggle-necessary"
                            />
                            <Toggle
                                label="Functional"
                                desc="Remember non-essential preferences. Currently NOT IN USE — toggling has no immediate effect."
                                checked={prefs.functional}
                                onChange={(v) => setPrefs((p) => ({ ...p, functional: v }))}
                                testid="cookie-toggle-functional"
                            />
                            <Toggle
                                label="Analytics"
                                desc="Anonymous traffic and product analytics. Currently NOT IN USE on public pages."
                                checked={prefs.analytics}
                                onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                                testid="cookie-toggle-analytics"
                            />
                            <Toggle
                                label="Marketing"
                                desc="Cross-site behavioral advertising. NOT IN USE — we do not run third-party ad trackers."
                                checked={prefs.marketing}
                                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                                testid="cookie-toggle-marketing"
                            />
                        </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                        {!customizing ? (
                            <>
                                <button
                                    type="button"
                                    onClick={acceptAll}
                                    disabled={busy}
                                    className="btn btn-primary flex-1"
                                    data-testid="cookie-banner-accept-all"
                                >
                                    Accept all
                                </button>
                                <button
                                    type="button"
                                    onClick={rejectAll}
                                    disabled={busy}
                                    className="btn btn-ghost flex-1 border border-ink"
                                    data-testid="cookie-banner-reject-all"
                                >
                                    Reject non-essential
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    onClick={saveCustom}
                                    disabled={busy}
                                    className="btn btn-primary flex-1"
                                    data-testid="cookie-banner-save-custom"
                                >
                                    Save preferences
                                </button>
                                <button
                                    type="button"
                                    onClick={rejectAll}
                                    disabled={busy}
                                    className="btn btn-ghost flex-1 border border-ink"
                                    data-testid="cookie-banner-reject-all-from-customize"
                                >
                                    Reject non-essential
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Toggle({ label, desc, checked, onChange, disabled, testid }) {
    return (
        <label
            className={`flex items-start gap-3 p-3 border border-ink-200 ${disabled ? "opacity-70" : "cursor-pointer hover:border-ink"}`}
            data-testid={testid}
        >
            <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={(e) => onChange?.(e.target.checked)}
                className="mt-1 h-4 w-4"
                data-testid={`${testid}-input`}
            />
            <div>
                <div className="text-sm font-semibold">{label}</div>
                <div className="text-xs text-ink-600 leading-relaxed">{desc}</div>
            </div>
        </label>
    );
}
